import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Navigate, useParams } from "react-router-dom";
import { Icon, type IconName } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { cn } from "@/lib/utils";
import { ProfileSection } from "./sections/ProfileSection";
import { SecuritySection } from "./sections/SecuritySection";
import { NotificationsSection } from "./sections/NotificationsSection";
import { PreferencesSection } from "./sections/PreferencesSection";
import { BillingSection } from "./sections/BillingSection";
import { InvoicingSection } from "./sections/InvoicingSection";
import { TeamSection } from "./sections/TeamSection";
import { BackOfficeTeamSection } from "./sections/BackOfficeTeamSection";

/**
 * Settings shell (TW-3463). A sectioned account screen with a sticky left sub-nav —
 * Profile · Security · Notifications · Preferences — built on the shared `.settings-layout` /
 * `.set-nav` Component Kit vocabulary. It mounts inside RoleLayout, so the same screen serves
 * both the Club and Back Office shells. The active section is driven by the `/settings/:section`
 * URL param, so each section is deep-linkable and refresh-stable, and the four sub-section
 * stories (TW-3458 / 3464 / 3465 / 3466) own a section body each.
 */

interface SectionDef {
  id: string;
  icon: IconName;
  labelKey: string;
  render: () => ReactNode;
  /** Only shown when this predicate (given the authz context) is true; default: always. */
  visible?: (ctx: {
    isClubUser: boolean;
    isBackOffice: boolean;
    canManageUsers: boolean;
    canManageBilling: boolean;
    canManageInvoiceSettings: boolean;
  }) => boolean;
}

const SECTIONS: SectionDef[] = [
  { id: "profile", icon: "user", labelKey: "settings.nav.profile", render: () => <ProfileSection /> },
  { id: "security", icon: "settings", labelKey: "settings.nav.security", render: () => <SecuritySection /> },
  {
    id: "notifications",
    icon: "bell",
    labelKey: "settings.nav.notifications",
    render: () => <NotificationsSection />,
    // Notification preferences are a back-office concern only (TRAV-2).
    visible: ({ isBackOffice }) => isBackOffice,
  },
  { id: "preferences", icon: "globe", labelKey: "settings.nav.preferences", render: () => <PreferencesSection /> },
  {
    id: "billing",
    icon: "invoices",
    labelKey: "settings.nav.billing",
    render: () => <BillingSection />,
    // Club self-service billing — club users who can manage their own club's billing details.
    visible: ({ isClubUser, canManageBilling }) => isClubUser && canManageBilling,
  },
  {
    id: "team",
    icon: "roster",
    labelKey: "settings.nav.team",
    render: () => <TeamSection />,
    // Club self-service team management — club users who can manage their own members only.
    visible: ({ isClubUser, canManageUsers }) => isClubUser && canManageUsers,
  },
  {
    // Same id as the club variant on purpose: both audiences reach their team screen at
    // /settings/team, and the disjoint visibility predicates mean only one ever renders.
    id: "team",
    icon: "roster",
    labelKey: "settings.nav.team",
    render: () => <BackOfficeTeamSection />,
    // Back-office team management — TC admins who can manage users (Back Office Admin role).
    visible: ({ isBackOffice, canManageUsers }) => isBackOffice && canManageUsers,
  },
  {
    id: "invoicing",
    icon: "invoices",
    labelKey: "settings.nav.invoicing",
    render: () => <InvoicingSection />,
    // Back-office org-wide invoicing config (issuer details + numbering) — TW-3527.
    visible: ({ isBackOffice, canManageInvoiceSettings }) => isBackOffice && canManageInvoiceSettings,
  },
];

/** Bare `/settings` and unknown sections fall back here (the first sub-nav item). */
const DEFAULT_SECTION = "profile";

export function SettingsPage() {
  const { t } = useTranslation();
  const { section } = useParams<{ section: string }>();
  const { me, has } = useAuthz();

  const ctx = {
    isClubUser: me?.userType === "CLUB_USER",
    isBackOffice: me?.userType === "BACK_OFFICE",
    canManageUsers: has("user.manage"),
    canManageBilling: has("billing.manage"),
    canManageInvoiceSettings: has("invoice.settings"),
  };
  const sections = SECTIONS.filter((s) => s.visible?.(ctx) ?? true);

  const active = sections.find((s) => s.id === section);
  // Unknown (or missing, or now-hidden) section → fall back to the default section, keeping the URL canonical.
  if (!active) return <Navigate to={`/settings/${DEFAULT_SECTION}`} replace />;

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t("settings.title")}</h1>
        <p className="muted">{t("settings.subtitle")}</p>
      </div>

      <div className="settings-layout">
        <nav className="set-nav" aria-label={t("settings.title")}>
          {sections.map((s) => (
            <NavLink
              key={s.id}
              to={`/settings/${s.id}`}
              className={({ isActive }) => cn("set-nav-item", isActive && "active")}
            >
              <Icon name={s.icon} size={18} />
              <span>{t(s.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div>{active.render()}</div>
      </div>
    </div>
  );
}
